import { ServiceFormValues } from '@/components/services_tsx/service-schema';
import { RealtimePostgresUpdatePayload, RealtimePostgresDeletePayload } from '@supabase/supabase-js';
import supabase from './supabase';

// Types for API responses
export interface Service extends ServiceFormValues {
  id: number;
  position: number;
}

// Query keys for Tanstack Query
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (filters: string) => [...serviceKeys.lists(), { filters }] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: number) => [...serviceKeys.details(), id] as const,
};

// Function to get all services from the database
export async function getServices(): Promise<Service[]> {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('position', { ascending: true });

  if (error) {
    console.error('Error loading services:', error);
    throw new Error('Services could not be loaded');
  }

  return data;
}

// Function to get a single service by ID
export async function getServiceById(id: number): Promise<Service> {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error loading service:', error);
    throw new Error('Service could not be loaded');
  }

  return data;
}

// Function to create or edit a service
export async function createEditService(
  service: Partial<ServiceFormValues>,
  id?: number
): Promise<Service> {
  const query = supabase.from('services');

  try {
    if (id) {
      // Update existing service
      const { data, error } = await query.update(service).eq('id', id).select().single();

      if (error) throw error;
      return data;
    } else {
      // For new service, get the max position and increment
      const { data: services, error: fetchError } = await query
        .select('position')
        .order('position', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      const maxPosition = services?.[0]?.position || 0;
      const newService = { ...service, position: maxPosition + 1 };

      // Create new service
      const { data, error } = await query.insert(newService).select().single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error in createEditService:', error);
    throw error;
  }
}

// Function to delete a service
export async function deleteService(id: number): Promise<{ success: boolean }> {
  const { error } = await supabase.from('services').delete().eq('id', id);

  if (error) {
    console.error('Error deleting service:', error);
    throw new Error('Service could not be deleted');
  }

  return { success: true };
}

// Function to swap service positions
export async function swapServicePositions(
  currentRow: Service,
  targetRow: Service,
  direction: 'up' | 'down'
): Promise<void> {
  try {
    // First, get all services to determine their positions
    const { data: services, error: fetchError } = await supabase
      .from('services')
      .select('*')
      .order('position');

    if (fetchError) throw fetchError;

    // Find the current positions
    const currentPosition = services?.find(svc => svc.id === currentRow.id)?.position || 0;
    const targetPosition = services?.find(svc => svc.id === targetRow.id)?.position || 0;

    // Update current row's position
    const { error: error1 } = await supabase
      .from('services')
      .update({ position: targetPosition })
      .eq('id', currentRow.id);

    if (error1) throw error1;

    // Update target row's position
    const { error: error2 } = await supabase
      .from('services')
      .update({ position: currentPosition })
      .eq('id', targetRow.id);

    if (error2) throw error2;
  } catch (error) {
    console.error(`Error swapping services ${direction}:`, error);
    throw error;
  }
}

// Supabase realtime subscription setup
export function subscribeToServices(
  onUpdate: (payload: { new: Service; old: Service }) => void,
  onDelete: (payload: { old: Service }) => void
): () => void {
  const channel = supabase
    .channel('services_changes')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'services',
      },
      (payload: RealtimePostgresUpdatePayload<Service>) => {
        if (payload.new && payload.old) {
          onUpdate({ new: payload.new as Service, old: payload.old as Service });
        }
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'services',
      },
      (payload: RealtimePostgresDeletePayload<Service>) => {
        if (payload.old) {
          onDelete({ old: payload.old as Service });
        }
      }
    );

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log('Subscribed to services changes');
    }
  });

  return () => {
    channel.unsubscribe();
  };
}
